import { useCallback, useEffect, useRef, useState } from 'react';
import {
  api,
  type DeviceRow,
  type HistorySample,
  type PriceInfo,
  type SpaMode,
  type Weather,
} from '../api';
import { subscribeUart, type UartMqttHandle } from '../uartMqtt';
import { Chart } from './Chart';

export { Chart };

const MODES: { id: SpaMode; label: string; blurb: string }[] = [
  { id: 'idle', label: 'Idle', blurb: 'No heating at all' },
  { id: 'pending', label: 'Pending', blurb: 'Hold 25°C, heat when power is cheap' },
  { id: 'ready', label: 'Ready', blurb: 'Hold full temperature' },
];

// Rectangular SP800 overlay, left-to-right, top-to-bottom. `key` is the
// protocol name the firmware expects; the label is what is printed on the pad.
const SP800_KEYS: { key: string; label: string }[] = [
  { key: 'light_intensity', label: 'Light Level' },
  { key: 'blower', label: 'Blower' },
  { key: 'pump_or_aux_a', label: 'Aux' },
  { key: 'down', label: 'Down' },
  { key: 'scroll', label: 'Scroll' },
  { key: 'up', label: 'Up' },
  { key: 'pump1b', label: 'Pump 1B' },
  { key: 'pump1a', label: 'Pump 1A' },
  { key: 'light_mode', label: 'Light Effect' },
];

type PanelSnap = {
  l1?: string;
  l2?: string;
  num?: string;
  numBlink?: boolean;
  flash?: boolean;
  numFlash?: number;
  l1Flash?: number;
  l2Flash?: number;
  level?: number;
  pump1a?: string;
  pump1b?: string;
  pump1?: string;
  pump1Output?: string;
  pump1Auto?: boolean;
  aux?: string;
  blower?: string;
  light?: string;
  heater?: string;
  sleep?: boolean;
  rgb?: number[];
};

function ledTone(value: string | boolean | undefined) {
  if (value === undefined) return 'unknown';
  if (value === true || value === 'high' || value === 'red' || value === 'manual_high' || value === 'auto_high') {
    return 'red';
  }
  if (value === 'auto' || value === 'auto_idle') return 'auto';
  if (
    value === 'on' ||
    value === 'low' ||
    value === 'ramping' ||
    value === 'green' ||
    value === 'manual_low' ||
    value === 'auto_running_low'
  ) {
    return 'green';
  }
  if (value === 'medium' || value === 'speed' || value === 'orange') return 'orange';
  if (value === 'unknown') return 'unknown';
  if (value === false || value === 'off') return 'off';
  return 'green';
}

function ledText(value: string | boolean | undefined) {
  if (value === undefined) return '—';
  if (value === true) return 'on';
  if (value === false) return 'off';
  return value.replaceAll('_', ' ');
}

function Led({
  label,
  value,
  text,
}: {
  label: string;
  value: string | boolean | undefined;
  text?: string;
}) {
  const tone = ledTone(value);
  return (
    <span className={`sp800-led ${tone}`}>
      <i aria-hidden="true" />
      <span className="sp800-led-label">{label}</span>
      <span className="sp800-led-mode">{ledText(text ?? value)}</span>
    </span>
  );
}

function LcdLine({ text, mask, active }: { text: string; mask: number; active: boolean }) {
  const cells = Array.from(text.padEnd(15)).slice(0, 15);
  while (cells.length < 15) cells.push(' ');
  return (
    <div className="lcd-line">
      {cells.map((ch, i) => (
        <span key={i} className={active && (mask & (1 << i)) !== 0 ? 'blink' : undefined}>
          {ch}
        </span>
      ))}
    </div>
  );
}

function LcdNum({
  value,
  mask,
  active,
  fallbackBlink,
}: {
  value: string;
  mask: number;
  active: boolean;
  fallbackBlink: boolean;
}) {
  const chars = Array.from(value);
  const digitAt = new Map<number, number>();
  let digit = 0;
  chars.forEach((ch, i) => {
    if (digit < 4 && (/\d/.test(ch) || ch === '-' || ch === '—')) {
      digitAt.set(i, digit);
      digit += 1;
    }
  });
  const perDigit = active && mask !== 0;
  return (
    <span className={fallbackBlink && !perDigit ? 'blink' : undefined}>
      {chars.map((ch, i) => {
        const d = digitAt.get(i);
        const blink = perDigit && d != null && (mask & (1 << d)) !== 0;
        return (
          <span key={i} className={blink ? 'blink' : undefined}>{ch}</span>
        );
      })}
    </span>
  );
}

// Standard WiFi RSSI bands. dBm is logarithmic and negative, which reads
// backwards to most people - -30 is excellent and -85 is nearly unusable - so
// always show the plain-English band next to the number.
export function signalBand(rssi?: number): { label: string; cls: string; bars: number } {
  if (rssi == null || rssi === 0) return { label: 'unknown', cls: 'sub', bars: 0 };
  if (rssi >= -50) return { label: 'excellent', cls: 'ok', bars: 4 };
  if (rssi >= -60) return { label: 'good', cls: 'ok', bars: 3 };
  if (rssi >= -70) return { label: 'fair', cls: 'warn', bars: 2 };
  // Below -80 a 2.4GHz link drops packets and throughput collapses; the spa
  // would still work but OTA updates start failing.
  if (rssi >= -80) return { label: 'weak', cls: 'warn', bars: 1 };
  return { label: 'very weak', cls: 'bad', bars: 1 };
}

export function SignalBars({ bars, cls }: { bars: number; cls: string }) {
  return (
    <span className={`bars ${cls}`} aria-hidden="true">
      {[1, 2, 3, 4].map((i) => (
        <i key={i} className={i <= bars ? 'on' : ''} style={{ height: `${3 + i * 2}px` }} />
      ))}
    </span>
  );
}

function fmtUptime(s?: number) {
  if (s == null) return '—';
  const d = Math.floor(s / 86400);
  const h = Math.floor((s % 86400) / 3600);
  const m = Math.floor((s % 3600) / 60);
  return [d && `${d}d`, (d || h) && `${h}h`, `${m}m`].filter(Boolean).join(' ');
}

// Wholesale RRP is $/MWh; c/kWh is the number people actually recognise.
const cKWh = (rrp: number | null | undefined) =>
  rrp == null ? '—' : `${(rrp / 10).toFixed(1)} c/kWh`;

// Amber already quotes retail cents per kWh, GST in.
const fmtCents = (c: number | null | undefined) =>
  c == null ? '—' : `${c.toFixed(1)} c/kWh`;

const DESCRIPTOR_LABEL: Record<string, string> = {
  negative: 'negative',
  extremelyLow: 'extremely low',
  veryLow: 'very low',
  low: 'low',
  neutral: 'normal',
  high: 'high',
  spike: 'spike',
};

const PERIOD_LABEL: Record<string, string> = {
  offPeak: 'off-peak',
  shoulder: 'shoulder',
  solarSponge: 'solar sponge',
  peak: 'peak',
};

function priceBand(price: PriceInfo | null): { label: string; cls: string } {
  const desc = price?.amber?.current?.general?.descriptor;
  if (desc) {
    const label = DESCRIPTOR_LABEL[desc] ?? desc;
    if (desc === 'spike' || desc === 'high') return { label, cls: 'bad' };
    if (desc === 'neutral') return { label, cls: 'warn' };
    return { label, cls: 'ok' };
  }
  if (!price?.current || !price.percentiles?.p25) return { label: 'unknown', cls: 'sub' };
  if (price.current <= price.percentiles.p25) return { label: 'cheap', cls: 'ok' };
  if (price.current >= price.percentiles.p75) return { label: 'expensive', cls: 'bad' };
  return { label: 'normal', cls: 'warn' };
}

function minutesLeft(endTime?: string) {
  if (!endTime) return null;
  const ms = new Date(endTime).getTime() - Date.now();
  if (Number.isNaN(ms)) return null;
  return Math.max(0, Math.ceil(ms / 60_000));
}

function Touchpad({
  panel,
  live,
  reconnecting,
  onError,
  onRefresh,
  onEnsureMqtt,
  refreshing,
}: {
  panel: PanelSnap | null;
  live: boolean;
  reconnecting: boolean;
  onError: (msg: string) => void;
  onRefresh: () => void;
  onEnsureMqtt: () => Promise<void>;
  refreshing: boolean;
}) {
  const [held, setHeld] = useState<string | null>(null);
  const [note, setNote] = useState('');
  const chain = useRef<Promise<unknown>>(Promise.resolve());
  const heldKey = useRef<string | null>(null);
  const holdTimer = useRef<number | null>(null);
  const sentPress = useRef(false);

  // Clicks go as a single firmware tap (press+release locally). Press/release
  // over MQTT is only used after this delay — otherwise cloud RTT looks like a
  // hold and the controller gets an extra RR=02 before release arrives.
  const HOLD_BEFORE_PRESS_MS = 400;

  function send(key: string, action: 'tap' | 'press' | 'release', label: string) {
    chain.current = chain.current
      .then(async () => {
        void onEnsureMqtt()
          .then(() => api.refreshPanel())
          .catch(() => undefined);
        await api.command('spa_key', { key, action });
      })
      .then(() => {
        if (action !== 'press') setNote(`${label} ${action === 'tap' ? 'tapped' : 'sent'}.`);
      })
      .catch((err: unknown) => {
        onError(err instanceof Error ? err.message : String(err));
      });
  }

  function clearHoldTimer() {
    if (holdTimer.current != null) {
      window.clearTimeout(holdTimer.current);
      holdTimer.current = null;
    }
  }

  function release(key: string, label: string) {
    if (heldKey.current !== key) return;
    heldKey.current = null;
    setHeld(null);
    clearHoldTimer();
    if (sentPress.current) {
      sentPress.current = false;
      send(key, 'release', label);
    } else {
      send(key, 'tap', label);
    }
  }

  const rgb = panel?.rgb;
  const rgbCss = rgb && rgb.length >= 3
    ? `rgb(${rgb[0]}, ${rgb[1]}, ${rgb[2]})`
    : undefined;

  return (
    <div className="card" style={{ marginTop: '1rem' }}>
      <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: '1rem', flexWrap: 'wrap' }}>
        <h2 style={{ margin: 0 }}>SP800 touchpad</h2>
        <div style={{ display: 'flex', alignItems: 'center', gap: '.75rem', marginLeft: 'auto' }}>
          {reconnecting && (
            <span className="sub mqtt-reconnecting" aria-live="polite">Reconnecting…</span>
          )}
          <button type="button" className="action" disabled={refreshing} onClick={onRefresh}>
            {refreshing ? 'Refreshing…' : 'Refresh panel'}
          </button>
        </div>
      </div>
      <p className="sub" style={{ marginTop: '.35rem' }}>
        LCD and status lamps from the spa bus. Updates arrive when the panel changes; use Refresh
        to pull the latest snapshot. Click a key for a tap, or hold for repeats.
        The original pad stays connected; the controller waits 2.3&nbsp;ms of idle so it keeps priority.
      </p>
      <div className="sp800">
        <div className="lcd" aria-live="polite">
          {!live && !panel && !reconnecting && <div className="lcd-line sub">Waiting for panel…</div>}
          {reconnecting && !panel && <div className="lcd-line sub">Reconnecting…</div>}
          {(live || panel) && (
            <>
              <LcdLine text={panel?.l1 ?? ''} mask={panel?.l1Flash ?? 0} active={Boolean(panel?.flash)} />
              <LcdLine text={panel?.l2 ?? ''} mask={panel?.l2Flash ?? 0} active={Boolean(panel?.flash)} />
              <div className="lcd-num">
                <LcdNum
                  value={panel?.num || '————'}
                  mask={panel?.numFlash ?? 0}
                  active={Boolean(panel?.flash)}
                  fallbackBlink={Boolean(panel?.numBlink) && panel?.flash == null}
                />
                {panel?.level != null && panel.level >= 0 && (
                  <span className="lcd-level">{panel.level}%</span>
                )}
                {rgbCss && <span className="lcd-rgb" style={{ background: rgbCss }} />}
              </div>
            </>
          )}
        </div>
        <div className="sp800-leds" role="group" aria-label="Panel status lights">
          <Led label="Pump 1A" value={panel?.pump1a} text={panel?.pump1} />
          <Led label="Pump 1B" value={panel?.pump1b} text={panel?.pump1} />
          <Led label="Auto" value={panel?.pump1Auto ? 'auto' : panel?.pump1Auto === false ? 'off' : undefined} />
          <Led label="Aux" value={panel?.aux} />
          <Led label="Blower" value={panel?.blower} />
          <Led label="Light" value={panel?.light} />
          <Led label="Heater" value={panel?.heater} />
          <Led label="Sleep" value={panel?.sleep} />
        </div>
        <div className="keypad" role="group" aria-label="SP800 touchpad">
        {SP800_KEYS.map((k) => (
          <button
            key={k.key}
            type="button"
            className="keypad-key"
            aria-pressed={held === k.key}
            onPointerDown={(e) => {
              e.preventDefault();
              (e.currentTarget as HTMLButtonElement).setPointerCapture(e.pointerId);
              clearHoldTimer();
              heldKey.current = k.key;
              sentPress.current = false;
              setHeld(k.key);
              holdTimer.current = window.setTimeout(() => {
                holdTimer.current = null;
                if (heldKey.current !== k.key) return;
                sentPress.current = true;
                send(k.key, 'press', k.label);
              }, HOLD_BEFORE_PRESS_MS);
            }}
            onPointerUp={() => release(k.key, k.label)}
            onPointerCancel={() => release(k.key, k.label)}
            onLostPointerCapture={() => release(k.key, k.label)}
            onContextMenu={(e) => e.preventDefault()}
            onClick={(e) => {
              // Keyboard activation has detail 0 and never fired pointerdown.
              if (e.detail === 0) send(k.key, 'tap', k.label);
            }}
          >
            {k.label}
          </button>
        ))}
        </div>
      </div>
      {note && <p className="msg" style={{ marginBottom: 0 }}>{note}</p>}
    </div>
  );
}

export default function Status() {
  const [device, setDevice] = useState<DeviceRow | null>(null);
  const [samples, setSamples] = useState<HistorySample[]>([]);
  const [price, setPrice] = useState<PriceInfo | null>(null);
  const [weather, setWeather] = useState<Weather | null>(null);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const [panel, setPanel] = useState<PanelSnap | null>(null);
  const [panelLive, setPanelLive] = useState(false);
  const [panelEverLive, setPanelEverLive] = useState(false);
  const [panelRefreshing, setPanelRefreshing] = useState(false);
  const mqttRef = useRef<UartMqttHandle | null>(null);

  const ensureMqtt = useCallback(async () => {
    const handle = mqttRef.current;
    if (!handle) throw new Error('MQTT not ready');
    await handle.ensureConnected();
  }, []);

  const refreshPanel = useCallback(async () => {
    setPanelRefreshing(true);
    try {
      await api.refreshPanel();
      setError('');
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setPanelRefreshing(false);
    }
  }, []);

  const load = useCallback(async () => {
    try {
      const [d, h, p, w] = await Promise.all([
        api.device(),
        api.history(24),
        api.price().catch(() => null),
        api.weather().catch(() => null),
      ]);
      setDevice(d);
      setSamples(h.samples ?? []);
      setPrice(p);
      setWeather(w);
      setError('');
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    }
  }, []);

  useEffect(() => {
    load();
    const id = setInterval(load, 15000);
    return () => clearInterval(id);
  }, [load]);

  useEffect(() => {
    if (panelLive) setPanelEverLive(true);
  }, [panelLive]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const cfg = await api.iotConfig();
        if (cancelled) return;
        const topic = cfg.panelTopic || cfg.topic.replace(/\/uart$/, '/panel');
        mqttRef.current = await subscribeUart(
          { ...cfg, topic },
          (payload) => {
            try {
              setPanel(JSON.parse(payload) as PanelSnap);
              setPanelLive(true);
            } catch {
              /* ignore truncated frames */
            }
          },
          (err) => setError(err.message),
          (connected) => setPanelLive(connected)
        );
        if (cancelled) {
          mqttRef.current.disconnect();
          mqttRef.current = null;
        } else {
          await refreshPanel();
        }
      } catch (err) {
        if (!cancelled) {
          setError(err instanceof Error ? err.message : String(err));
        }
      }
    })();
    return () => {
      cancelled = true;
      mqttRef.current?.disconnect();
      mqttRef.current = null;
    };
  }, [refreshPanel]);

  useEffect(() => {
    const onVisible = () => {
      if (document.visibilityState !== 'visible') return;
      mqttRef.current?.ensureConnected().catch(() => undefined);
    };
    document.addEventListener('visibilitychange', onVisible);
    return () => document.removeEventListener('visibilitychange', onVisible);
  }, []);

  const refreshPanelLive = useCallback(async () => {
    void ensureMqtt().catch(() => undefined);
    await refreshPanel();
  }, [ensureMqtt, refreshPanel]);

  const latest = samples.at(-1);
  const mode = (latest?.mode ?? 'idle') as SpaMode;
  const linkAbsent = (latest?.link ?? 'absent') !== 'connected';
  const band = priceBand(price);
  const amberGeneral = price?.amber?.current?.general;
  const amberNext = price?.amber?.forecast?.general?.[0];
  const amberLeft = minutesLeft(amberGeneral?.endTime);

  async function setMode(next: SpaMode) {
    setBusy(true);
    try {
      await api.setSpa({ mode: next });
      await load();
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy(false);
    }
  }

  return (
    <>
      {linkAbsent && (
        <div className="banner">
          <strong>Spa not connected.</strong> The controller is not yet wired to the SP1200,
          so every spa reading below is <em>simulated</em> by the stub driver. Modes and
          targets are real and do round-trip to the device — only the water temperature is
          make-believe.
        </div>
      )}
      {error && <div className="banner"><strong>Error.</strong> {error}</div>}

      <div className="grid">
        <div className="card">
          <h2>Water</h2>
          <div className="big">
            {latest?.waterTempC != null ? `${latest.waterTempC.toFixed(2)}°` : '—'}
          </div>
          <p className="sub">
            target {latest?.targetTempC ?? '—'}° · heater{' '}
            <span className={latest?.heater === 'high' ? 'bad' : latest?.heater === 'low' ? 'warn' : 'sub'}>
              {latest?.heater ?? '—'}
            </span>
            {latest?.heater === 'low' && ' (load shedding)'}
          </p>
        </div>

        <div className="card">
          <h2>Power price ({price?.amber ? 'Amber' : (price?.region ?? 'NSW1')})</h2>
          <div className="big">
            {amberGeneral ? fmtCents(amberGeneral.perKwh) : cKWh(price?.current)}
          </div>
          <p className="sub">
            <span className={band.cls}>{band.label}</span>
            {amberGeneral?.tariff?.period &&
              ` · ${PERIOD_LABEL[amberGeneral.tariff.period] ?? amberGeneral.tariff.period}`}
            {amberGeneral?.renewables != null &&
              ` · ${Math.round(amberGeneral.renewables)}% renewable`}
            {!price?.amber && price?.percentiles?.p50 != null && ` · typical ${cKWh(price.percentiles.p50)}`}
          </p>
          {amberGeneral && (
            <p className="sub" style={{ marginBottom: 0 }}>
              {amberLeft != null && `${amberLeft} min left`}
              {amberNext && ` · next ${fmtCents(amberNext.perKwh)}`}
              {amberGeneral.spotPerKwh != null && ` · spot ${fmtCents(amberGeneral.spotPerKwh)}`}
            </p>
          )}
        </div>

        <div className="card">
          <h2>Outside</h2>
          <div className="big">{weather?.tempC != null ? `${weather.tempC.toFixed(1)}°` : '—'}</div>
          <p className="sub">
            feels {weather?.apparentC?.toFixed(1) ?? '—'}° · {weather?.humidity ?? '—'}% RH ·{' '}
            {weather?.rainMm ?? 0} mm · {weather?.windKph ?? '—'} km/h
          </p>
        </div>
      </div>

      <div className="card" style={{ marginTop: '1rem' }}>
        <h2>Mode</h2>
        <div className="modes">
          {MODES.map((m) => (
            <button key={m.id} aria-pressed={mode === m.id} disabled={busy}
                    onClick={() => setMode(m.id)}>
              {m.label}
              <small>{m.blurb}</small>
            </button>
          ))}
        </div>
        {latest?.reason && <p className="sub" style={{ marginBottom: 0 }}>{latest.reason}</p>}
      </div>

      <Touchpad
        panel={panel}
        live={panelLive}
        reconnecting={!panelLive && panelEverLive}
        onError={setError}
        onRefresh={refreshPanelLive}
        onEnsureMqtt={ensureMqtt}
        refreshing={panelRefreshing}
      />

      <div className="card" style={{ marginTop: '1rem' }}>
        <h2>History and forecast</h2>
        <Chart
          samples={samples}
          amberForecast={price?.amber?.forecast?.general}
          weatherForecast={weather?.forecast}
        />
      </div>

      <div className="card" style={{ marginTop: '1rem' }}>
        <h2>Connectivity</h2>
        <table className="kv">
          <tbody>
            <tr><th>Device</th><td className={device?.online ? 'ok' : 'bad'}>
              {device?.online ? 'online' : 'offline'}</td></tr>
            <tr><th>WiFi</th><td>{device?.ssid ?? '—'}</td></tr>
            <tr><th>IP address</th><td>{device?.ip ?? '—'}</td></tr>
            <tr>
              <th>Signal</th>
              <td>
                {device?.rssi != null && device.rssi !== 0 ? (
                  <>
                    <SignalBars {...signalBand(device.rssi)} />
                    <span className={signalBand(device.rssi).cls}>
                      {signalBand(device.rssi).label}
                    </span>
                    <span className="sub"> · {device.rssi} dBm</span>
                  </>
                ) : (
                  '—'
                )}
              </td>
            </tr>
            <tr><th>Uptime</th><td>{fmtUptime(device?.uptimeSeconds)}</td></tr>
            <tr><th>Last seen</th><td>
              {device?.lastSeen ? new Date(device.lastSeen).toLocaleString() : '—'}</td></tr>
          </tbody>
        </table>
      </div>
    </>
  );
}
